import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  TextField,
  AddCta,
  Table,
  TableFooter,
  DateFilterModal,
  ResetFilterCta,
  Loader,
} from "components";
import { useSelector } from "react-redux";
import makeRequest from "services/makeRequest";
import { routes } from "routes/apiRoutes";
import { useNavigate } from "react-router-dom";
import { BiCalendar } from "react-icons/bi";
import debounce from "lodash.debounce";
import { ROUTES } from "routes/routePaths";
import { userStatuses } from "utils/constants";
import { permissions } from "utils/permissions";
import {
  isObjectsEqual,
  formatUrlQuery,
  formatFilterPayload,
  hasPermission,
} from "utils/helper";
import UserTypes from "./UserTypes";
import {
  getAgentColumns,
  getAggregatorColumns,
  getAdminColumns,
} from "./TableColumns";

const UserManagement = () => {
  const navigate = useNavigate();

  const { createAgent, createAdminUser } = ROUTES;
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);

  const user = useSelector((state) => state.user);
  const userAuthorities = user?.user?.authorities;

  const userTypes = [
    { label: "Agents", value: "AGENT_USER" },
    { label: "Aggregators", value: "AGGREGATOR_USER" },
    // { label: "Admins", value: "ADMIN_USER" },
  ];

  const initialFilterState = {
    number: 1,
    createdDateFrom: "",
    createdDateTo: "",
    status: "",
    username: "",
    userType: userTypes[0].value,
    size: 20,
    sorted: true,
  };

  const [filterState, setFilterState] = useState(initialFilterState);

  const {
    name,
    size,
    number,
    orderBy,
    createdDateFrom,
    createdDateTo,
    sorted,
    userType,
    status,
  } = filterState;

  const [data, setData] = useState({
    users: null,
    loading: false,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(name);
    if (name === "size") {
      //to handle edge case of already being on page 4 for example and now requesting for 100 data, if data on a page 4 is not up to 100,
      //  endpoint returns empty so we reset the page number to 1 to fetch data appropriately
      filterState.number = 1;
    }
    setFilterState({ ...filterState, [name]: value });
  };

  const getusersData = (queryParams) =>
    makeRequest("get", routes.fetchUsers(queryParams));

  const resetFilter = () => {
    setFilterState(initialFilterState);
    loadUsers();
  };

  const handleSearch = () => {
    loadWithDebounce();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadWithDebounce = useCallback(
    debounce((name) => {
      // if (name === "") {
      //   loadUsers();
      // } else {
      //   loadUsers(formatUrlQuery());
      // }
    }, 1000),
    []
  );

  const loadUsers = async (
    queryParams = formatUrlQuery(formatFilterPayload(filterState))
  ) => {
    setData({ ...data, loading: true });
    try {
      const res = await getusersData(queryParams);
      setData((prev) => ({
        ...prev,
        users: res.data.data,
        loading: false,
      }));
    } catch (err) {
      setData((prev) => ({
        ...prev,
        loading: false,
      }));
      console.log(err);
    }
  };

  useEffect(() => {
    loadUsers();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [number, size, orderBy, userType, sorted, status]);

  return (
    <div className="dashboard-page-container">
      <Typography variant="subtitle3" component="h4" state="primary">
        All User Types
      </Typography>
      <UserTypes />
      <div className="d-flex justify-content-end" style={{ marginTop: "4rem" }}>
        {hasPermission(permissions.canCreateUser, userAuthorities) && (
          <AddCta
            className="sm-margin mb-4"
            text={`Create an ${userType === "ADMIN_USER" ? "Admin" : "Agent"}`}
            onClick={() =>
              navigate(
                userType === "ADMIN_USER" ? createAdminUser : createAgent
              )
            }
          />
        )}
      </div>
      <div className="d-md-flex justify-content-between align-items-center">
        <div className="d-md-flex align-items-center">
          <div className="d-flex align-items-center table-header-filters px-3">
            <Typography state="grey" variant="body1">
              Sort by:
            </Typography>
            <select
              name="sort"
              value={sorted}
              className="table-header-select table-header-order"
              onChange={(e) =>
                setFilterState({
                  ...filterState,
                  sorted: JSON.parse(e.target.value),
                })
              }
            >
              <option value={true}>Ascending</option>
              <option value={false}>Descending</option>
            </select>
            <select
              name="status"
              value={status}
              onChange={handleChange}
              className="table-header-select table-header-order"
            >
              <option value="">All</option>
              {userStatuses.map(({ label, mapper, value }, key) => (
                <option key={key} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <div
              className="cursor-pointer"
              onClick={() => setIsDateModalOpen(true)}
            >
              <Typography component={"span"}>Date Created</Typography>{" "}
              <BiCalendar fontSize="20px" style={{ marginTop: "-.2rem" }} />
            </div>
          </div>
        </div>
        <TextField
          name="name"
          id="name"
          value={name}
          onChange={handleSearch}
          placeholder={`${
            userType === "ADMIN_USER" ? "Admin's" : "Agent's"
          } Username`}
          size="sm"
          required={false}
          searchField
          className="tiny-bordered-textfield"
          containerClassName={"sm-margin d-none"} //remove d-none when to be implemented
        />
      </div>
      <div style={{ margin: "3rem 0 1.5rem" }} className="row">
        <div className="col-lg-10">
          <ul className="d-flex user-type-headers">
            {userTypes.map(({ label, value }, key) => (
              <li
                key={key}
                className={userType === value ? "active-user-header" : ""}
                onClick={() => {
                  setFilterState({
                    ...initialFilterState,
                    userType: value,
                  });
                  filterState.number = 1;
                }}
              >
                <Typography state="primary" variant="body0">
                  All {label}
                </Typography>
              </li>
            ))}

            {data.loading && (
              <span className="d-none d-lg-block">
                <Loader height={20} width={2} />
              </span>
            )}
          </ul>
        </div>
        <div className="col-lg-2 d-none d-lg-flex justify-content-end">
          {!isObjectsEqual(initialFilterState, filterState) && (
            <ResetFilterCta onClick={resetFilter} />
          )}
        </div>
      </div>
      <div>
        <Table
          columns={
            userType === "AGENT_USER"
              ? getAgentColumns(filterState.number)
              : userType === "AGGREGATOR_USER"
              ? getAggregatorColumns(filterState.number)
              : getAdminColumns(filterState.number)
          }
          data={data?.users?.content || []}
          loading={data.loading}
        />
        {data?.users?.content?.length ? (
          <TableFooter
            pageSize={filterState.size}
            handleChange={handleChange}
            availableDataLength={data?.users?.content?.length}
            totalDataLength={data.users.totalElements}
            handlePageClick={(pageNo) =>
              setFilterState({ ...filterState, number: pageNo })
            }
          />
        ) : (
          ""
        )}
        <DateFilterModal
          openDateModal={isDateModalOpen}
          setOpenDateModal={() => setIsDateModalOpen(!isDateModalOpen)}
          handleChange={handleChange}
          startDate={createdDateFrom}
          endDate={createdDateTo}
          startDateName={"createdDateFrom"}
          endDateName={"createdDateTo"}
          onButtonClick={() => {
            setIsDateModalOpen(false);
            loadUsers();
          }}
        />
      </div>
    </div>
  );
};

export default UserManagement;
